import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Users, Plus, X, Edit3, Trash2, Save, Loader2, Mail
} from "lucide-react";

const TeamView = () => {
  const { userId } = useParams();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    callSign: "",
    email: "",
    avatar: ""
  });

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/${userId}`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error("Error fetching team:", error);
      // Initialize with empty array if endpoint doesn't exist
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [userId]); // eslint-disable-line

  const handleAddMember = async () => {
    if (!formData.name.trim() || !formData.callSign.trim()) {
      toast.error("Name and Call Sign are required");
      return;
    }

    setSaving(true);
    try {
      const memberData = {
        userId,
        name: formData.name.trim(),
        callSign: formData.callSign.trim(),
        email: formData.email.trim(),
        avatar: formData.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${formData.name}`
      };

      const response = await axios.post(`${API}/team`, memberData);
      setTeamMembers(prev => [...prev, response.data]);
      resetForm();
      toast.success("Team member added!");
    } catch (error) {
      console.error("Error adding team member:", error);
      toast.error("Failed to add team member");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async (memberId) => {
    if (!formData.name.trim() || !formData.callSign.trim()) {
      toast.error("Name and Call Sign are required");
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        callSign: formData.callSign.trim(),
        email: formData.email.trim(),
        avatar: formData.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${formData.name}`
      };

      await axios.put(`${API}/team/${memberId}`, updateData);
      setTeamMembers(prev => prev.map(m => 
        m.memberId === memberId ? { ...m, ...updateData } : m
      ));
      resetForm();
      toast.success("Team member updated!");
    } catch (error) {
      console.error("Error updating team member:", error);
      toast.error("Failed to update team member");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm("Are you sure you want to remove this team member?")) return;

    try {
      await axios.delete(`${API}/team/${memberId}`);
      setTeamMembers(prev => prev.filter(m => m.memberId !== memberId));
      toast.success("Team member removed");
    } catch (error) {
      console.error("Error deleting team member:", error);
      toast.error("Failed to remove team member");
    }
  };

  const startEdit = (member) => {
    setEditingId(member.memberId);
    setFormData({
      name: member.name,
      callSign: member.callSign,
      email: member.email || "",
      avatar: member.avatar || ""
    });
    setShowAddForm(false);
  };

  const resetForm = () => {
    setFormData({ name: "", callSign: "", email: "", avatar: "" });
    setShowAddForm(false);
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#3dd9c5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-white mb-2 flex items-center gap-2">
            <Users className="h-6 w-6 text-neon-cyan" />
            <span className="text-neon-cyan">Team</span>
          </h1>
          <p className="text-sm text-gray-500">Manage your sales team members</p>
        </div>
        <Button
          onClick={() => { setShowAddForm(true); setEditingId(null); setFormData({ name: "", callSign: "", email: "", avatar: "" }); }}
          className="bg-gradient-to-r from-pink-600/80 to-pink-500/80 hover:from-pink-500/80 hover:to-pink-400/80 text-white"
          data-testid="add-team-member-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingId) && (
        <Card className="bg-[#0f1419] border-gray-800/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? "Edit Team Member" : "Add New Team Member"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                className="bg-white/5 border-gray-700 text-white"
                data-testid="member-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Call Sign *</label>
              <Input
                value={formData.callSign}
                onChange={(e) => setFormData(prev => ({ ...prev, callSign: e.target.value }))}
                placeholder="Maverick"
                className="bg-white/5 border-gray-700 text-white"
                data-testid="member-callsign-input"
              />
              <p className="text-xs text-gray-500 mt-1">A fun nickname for the team member</p>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email</label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@company.com"
                className="bg-white/5 border-gray-700 text-white"
                data-testid="member-email-input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Avatar URL (optional)</label>
              <Input
                value={formData.avatar}
                onChange={(e) => setFormData(prev => ({ ...prev, avatar: e.target.value }))}
                placeholder="https://..."
                className="bg-white/5 border-gray-700 text-white"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={() => editingId ? handleUpdateMember(editingId) : handleAddMember()}
              disabled={saving}
              className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
              data-testid="save-member-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingId ? "Update" : "Add"} Member
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-white/10"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {teamMembers.map((member) => (
          <TeamMemberCard
            key={member.memberId}
            member={member}
            onEdit={() => startEdit(member)}
            onDelete={() => handleDeleteMember(member.memberId)}
          />
        ))}
        
        {teamMembers.length === 0 && !showAddForm && (
          <Card className="bg-[#0f1419] border-gray-800/50 border-dashed p-8 col-span-full text-center">
            <Users className="h-16 w-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-400 mb-2">No team members yet</h3>
            <p className="text-gray-500 text-sm mb-4">Add your first team member to get started</p>
            <Button
              onClick={() => setShowAddForm(true)}
              variant="outline"
              className="border-[#3dd9c5]/50 text-[#3dd9c5] hover:bg-[#3dd9c5]/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Team Member
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

// Team Member Card Component with Hover Effects
const TeamMemberCard = ({ member, onEdit, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      className="bg-[#0f1419] border-gray-800/50 p-6 relative overflow-hidden transition-all duration-300 hover:border-[#3dd9c5]/50 hover:shadow-lg hover:shadow-[#3dd9c5]/10"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`team-member-card-${member.memberId}`}
    >
      {/* Avatar with hover enlarge */}
      <div className="flex justify-center mb-4">
        <div 
          className={`rounded-full overflow-hidden border-2 border-[#3dd9c5]/30 transition-all duration-300 ${
            isHovered ? 'w-24 h-24 border-[#3dd9c5]' : 'w-20 h-20'
          }`}
        >
          <img
            src={member.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${member.name}`}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Name and Call Sign */}
      <div className="text-center">
        <h3 className="text-lg font-bold text-white mb-1">{member.name}</h3>
        <p className="text-[#3dd9c5] font-medium text-sm">&ldquo;{member.callSign}&rdquo;</p>
      </div>

      {/* Email - shows on hover */}
      <div 
        className={`mt-4 text-center overflow-hidden transition-all duration-300 ${
          isHovered ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {member.email && (
          <a 
            href={`mailto:${member.email}`}
            className="flex items-center justify-center gap-2 text-gray-400 hover:text-[#3dd9c5] text-sm"
          >
            <Mail className="h-4 w-4" />
            {member.email}
          </a>
        )}
      </div>

      {/* Action Buttons - show on hover */}
      <div 
        className={`absolute top-3 right-3 flex gap-1 transition-all duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button
          onClick={onEdit}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white transition-colors"
          title="Edit"
        >
          <Edit3 className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Decorative gradient on hover */}
      <div 
        className={`absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-[#e91e8c] to-[#3dd9c5] transition-all duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </Card>
  );
};

export default TeamView;
